// DevHub UI kit — shared section banner (hero).
//
// Extracted verbatim from HubPage's `devhub-hub__banner` (eyebrow + title + intro
// + optional CTA buttons over a full-bleed photo + gradient overlay), so every
// section landing, docs/catalog page and hub renders one identical hero. This
// replaces the inline banner copies previously duplicated across those pages.
//
// Markup/classes are byte-identical to HubPage's banner so the visual is the same;
// the styling lives in the co-located SectionBanner.css (same tokens/selectors as
// HubPage.css).
import { useNavigate } from 'react-router-dom';
import networkHero from '../../assets/hero/network.jpg';
import './SectionBanner.css';

export interface SectionBannerCta {
  label: string;
  to: string;
  /** Primary (filled) treatment instead of the outlined default. */
  primary?: boolean;
  /** Open `to` in a new tab instead of navigating in-app. */
  external?: boolean;
}

export interface SectionBannerProps {
  /** Small uppercase label above the title (defaults to "Dräger DevHub"). */
  eyebrow?: string;
  title: string;
  intro?: string;
  ctas?: SectionBannerCta[];
  /**
   * Background photo. Defaults to the self-hosted "network" hero. Pass `null` to
   * render the banner with just the brand gradient (no photo).
   */
  image?: string | null;
}

// Fallback banner photo — self-hosted + commercially licensed (see
// docs/asset-licenses.md), bundled by the build and never hot-linked.
const DEFAULT_BANNER_IMAGE = networkHero;

export const SectionBanner = ({
  eyebrow = 'Dräger DevHub',
  title,
  intro,
  ctas,
  image = DEFAULT_BANNER_IMAGE,
}: SectionBannerProps) => {
  const navigate = useNavigate();

  const go = (to: string, external?: boolean) => {
    if (external) {
      window.open(to, '_blank', 'noopener');
    } else {
      navigate(to);
    }
  };

  return (
    <header className="devhub-hub__banner">
      {image && (
        <div
          className="devhub-hub__banner-photo"
          style={{ backgroundImage: `url("${image}")` }}
        />
      )}
      <div className="devhub-hub__banner-overlay" />
      <div className="devhub-hub__banner-content">
        {eyebrow && <span className="devhub-hub__eyebrow">{eyebrow}</span>}
        <h1 className="devhub-hub__title">{title}</h1>
        {intro && <p className="devhub-hub__intro">{intro}</p>}
        {ctas && ctas.length > 0 && (
          <div className="devhub-hub__ctas">
            {ctas.map(cta => (
              <button
                key={cta.label}
                type="button"
                className={`devhub-hub__cta ${cta.primary ? 'is-primary' : ''}`}
                onClick={() => go(cta.to, cta.external)}
              >
                {cta.label}
                <odx-icon name="core::chevron-right" size="sm" />
              </button>
            ))}
          </div>
        )}
      </div>
    </header>
  );
};
